import { action, makeAutoObservable, ObservableMap, ObservableSet } from "mobx";

import { mapToRecord } from "../../lib/conversion";

import { RIGHT_SIDEBAR_WIDTH } from "../../components/navigation/SidebarBase";
import Persistent from "../interfaces/Persistent";
import Store from "../interfaces/Store";

export interface Data {
    lastSection?: "home" | "server";
    lastHomePath?: string;
    lastOpened?: Record<string, string>;
    openSections?: Record<string, boolean>;
    rightSidebarWidth?: number;
}

export const SIDEBAR_MEMBERS = "sidebar_members";
export const SIDEBAR_CHANNELS = "sidebar_channels";
export const SIDEBAR_PINNED = "sidebar_pinned";
export const SECTION_MENTION = "mention";
export const SECTION_NSFW = "nsfw";

export const RIGHT_SIDEBAR_MIN_WIDTH = 280;
export const RIGHT_SIDEBAR_MAX_WIDTH = 900;

/**
 * Clamp the user-adjustable right sidebar width so the channel
 * area stays usable. The min narrows with the viewport so narrow
 * phones (e.g. 414px) keep a usable channel column.
 */
export function clampRightSidebarWidth(width: number): number {
    const min = Math.min(
        RIGHT_SIDEBAR_MIN_WIDTH,
        Math.round(window.innerWidth * 0.45),
    );
    const max = Math.max(
        min,
        Math.min(RIGHT_SIDEBAR_MAX_WIDTH, Math.round(window.innerWidth * 0.7)),
    );
    return Math.min(Math.max(Math.round(width), min), max);
}

/**
 * Keeps track of the last open channels, tabs, etc.
 * Handles providing good UX experience on navigating
 * back and forth between different parts of the app.
 */
export default class Layout implements Store, Persistent<Data> {
    /**
     * The last 'major section' that the user had open.
     * This is either the home tab or a channel ID (for a server channel).
     */
    private lastSection: "home" | "discover" | string;

    /**
     * The last path the user had open in the home tab.
     */
    private lastHomePath: string;

    /**
     * Volatile last discover path.
     */
    private lastDiscoverPath: string;

    /**
     * Map of last channels viewed in servers.
     */
    private lastOpened: ObservableMap<string, string>;

    /**
     * Map of section IDs to their current state.
     */
    private openSections: ObservableMap<string, boolean>;

    /**
     * Width (px) of the right sidebar, user-adjustable via drag.
     */
    rightSidebarWidth: number;

    /**
     * Channel where selection mode is active.
     */
    private selectionChannelId: string | undefined;

    /**
     * Set of selected message IDs.
     */
    private selectedMessages: ObservableSet<string>;

    /**
     * Construct new Layout store.
     */
    constructor() {
        this.lastSection = "home";
        this.lastHomePath = "/";
        this.lastDiscoverPath = "/discover/servers";
        this.lastOpened = new ObservableMap();
        this.openSections = new ObservableMap();
        this.rightSidebarWidth = RIGHT_SIDEBAR_WIDTH;
        this.selectionChannelId = undefined;
        this.selectedMessages = new ObservableSet();

        this.getLastHomePath = this.getLastHomePath.bind(this);

        makeAutoObservable(this, {
            hydrate: action,

            setLastOpened: action,

            setLastSection: action,
            setLastHomePath: action,
            setLastDiscoverPath: action,

            setSectionState: action,
            toggleSectionState: action,
            setRightSidebarWidth: action,

            enterSelection: action,
            exitSelection: action,
            toggleMessage: action,
        });
    }

    get id() {
        return "layout";
    }

    toJSON() {
        return {
            lastSection: this.lastSection,
            lastHomePath: this.lastHomePath,
            lastOpened: mapToRecord(this.lastOpened),
            openSections: mapToRecord(this.openSections),
            rightSidebarWidth: this.rightSidebarWidth,
        };
    }

    hydrate(data: Data) {
        if (data.lastSection) {
            this.lastSection = data.lastSection;
        }

        if (data.lastHomePath) {
            this.lastHomePath = data.lastHomePath;
        }

        if (data.lastOpened) {
            Object.keys(data.lastOpened).forEach((key) =>
                this.lastOpened.set(key, data.lastOpened![key]),
            );
        }

        if (data.openSections) {
            Object.keys(data.openSections).forEach((key) =>
                this.openSections.set(key, data.openSections![key]),
            );
        }

        if (
            typeof data.rightSidebarWidth === "number" &&
            Number.isFinite(data.rightSidebarWidth)
        ) {
            this.setRightSidebarWidth(data.rightSidebarWidth);
        }
    }

    /**
     * Get the last 'major section' the user had open.
     * @returns Last open section
     */ getLastSection() {
        return this.lastSection;
    }

    /**
     * Get last opened channel in a server.
     * @param server Server ID
     */ getLastOpened(server: string) {
        return this.lastOpened.get(server);
    }

    /**
     * Get the path to a server (as seen on sidebar).
     * @param server Server ID
     * @returns Pathname
     */ getServerPath(server: string) {
        let path = `/server/${server}`;
        if (this.lastOpened.has(server)) {
            path += `/channel/${this.getLastOpened(server)}`;
        }

        return path;
    }

    /**
     * Set last opened channel in a server.
     * @param server Server ID
     * @param channel Channel ID
     */ setLastOpened(server: string, channel: string) {
        this.lastOpened.set(server, channel);
        this.lastSection = server;
    }

    /**
     * Get the last path the user had open in the home tab.
     * @returns Last home path
     */ getLastHomePath() {
        return this.lastHomePath;
    }

    /**
     * Get the last path the user had open.
     * @returns Last path
     */ getLastPath() {
        return (
            (this.lastSection === "discover"
                ? this.lastDiscoverPath
                : this.lastSection === "home"
                  ? this.lastHomePath
                  : this.getServerPath(this.lastSection)!) ??
            this.lastHomePath ??
            "/"
        );
    }

    /**
     * Set the last opened section.
     * @param section Section name
     */ setLastSection(section: string) {
        this.lastSection = section;
    }

    /**
     * Set the current path open in the home tab.
     * @param path Pathname
     */ setLastHomePath(path: string) {
        if (path.startsWith("/bot")) return;
        if (path.startsWith("/invite")) return;

        this.lastHomePath = path;
        this.lastSection = "home";
    }

    /**
     * Set the last discover path.
     * @param path Pathname
     */ setLastDiscoverPath(path: string) {
        this.lastDiscoverPath = path;
        this.lastSection = "discover";
    }

    /**
     *
     * @param id Section ID
     * @returns Whether the section is open
     * @param def Default state value
     */ getSectionState(id: string, def?: boolean) {
        return this.openSections.get(id) ?? def ?? false;
    }

    /**
     * Set the state of a section.
     * @param id Section ID
     * @param value New state value
     * @param def Default state value
     */ setSectionState(id: string, value: boolean, def?: boolean) {
        if (value === def) {
            this.openSections.delete(id);
        } else {
            this.openSections.set(id, value);
        }
    }

    /**
     * Set the right sidebar width, clamped to sane bounds.
     */
    setRightSidebarWidth(width: number) {
        this.rightSidebarWidth = clampRightSidebarWidth(width);
    }

    /**
     * Toggle state of a section.
     * @param id Section ID
     * @param def Default state value
     */ toggleSectionState(id: string, def?: boolean) {
        this.setSectionState(id, !this.getSectionState(id, def), def);
    }

    enterSelection(channelId: string, initialMessageId?: string) {
        this.selectedMessages.clear();
        this.selectionChannelId = channelId;
        if (initialMessageId) {
            this.selectedMessages.add(initialMessageId);
        }
    }

    exitSelection() {
        this.selectionChannelId = undefined;
        this.selectedMessages.clear();
    }

    toggleMessage(messageId: string) {
        if (this.selectedMessages.has(messageId)) {
            this.selectedMessages.delete(messageId);
        } else {
            this.selectedMessages.add(messageId);
        }
    }

    isSelected(messageId: string) {
        return this.selectedMessages.has(messageId);
    }

    getSelectedCount() {
        return this.selectedMessages.size;
    }

    getSelectedMessages() {
        return [...this.selectedMessages];
    }

    getSelectionChannelId() {
        return this.selectionChannelId;
    }

    isInSelection(channelId: string | undefined) {
        return this.selectionChannelId === channelId;
    }
}
